import { asLink, type LinkField } from '@prismicio/client';
import { normalizeAnchor } from './anchor-link-utils';

/**
 * The registry of same-page modals, and the one rule for how Prismic content
 * points at them.
 *
 * Editors do not need to know which slice they are in: they fill any link/CTA
 * field the way they always do and use the modal's token as the URL fragment.
 *
 *   #modal-get-started              <- preferred
 *   /contact-us#modal-get-started  <- when a no-JS fallback destination helps
 *
 * Only the *fragment* is ever read, so the path and origin are free. Prefer the
 * bare fragment: `asLinkAttrs` emits a clean `href="#modal-get-started"` for it,
 * whereas an absolute `https://…` URL is classified as external (it picks up
 * `rel="noreferrer"`), and on preview/staging a click that somehow escaped
 * interception would leave the environment for production.
 *
 * A link carrying a modal token never navigates: the click is intercepted by
 * `useModalLinkDispatcher` and turned into a store action.
 *
 * The `modal-` prefix is a namespace, and it is what keeps this from colliding
 * with the same-page anchor feature in `anchor-link-utils` (whose tokens are
 * bare element ids and slice types) or with the real `/get-started` route,
 * which is a path and is never inspected.
 *
 * Adding a modal is one entry in `MODAL_IDS` plus one `<ModalHost>` — its
 * `#modal-<id>` token comes for free and no slice has to change.
 *
 * NOTE: like `anchor-link-utils`, deliberately not re-exported from `@/utils` —
 * that barrel pulls in `cached-settings` / `cached-pages`, which are
 * server-only. Import this module directly so client components don't drag
 * them into the bundle.
 */

export const MODAL_IDS = {
  getStarted: 'get-started',
} as const;

export type ModalId = (typeof MODAL_IDS)[keyof typeof MODAL_IDS];

/** Namespace prefix for the editor-facing token. */
const MODAL_FRAGMENT_PREFIX = 'modal-';

const IDS_BY_FRAGMENT = new Map<string, ModalId>(
  Object.values(MODAL_IDS).map((id) => [`${MODAL_FRAGMENT_PREFIX}${id}`, id]),
);

/**
 * Click-time resolution, from an anchor's raw `href`.
 *
 * Returns `null` for every ordinary link — including plain same-page anchors —
 * so callers can bail out before touching the event.
 */
export function resolveModalIdFromHref(
  href: string | null | undefined,
): ModalId | null {
  if (!href) {
    return null;
  }

  const hashIndex = href.indexOf('#');
  if (hashIndex === -1) {
    return null;
  }

  const fragment = normalizeAnchor(href.slice(hashIndex));
  if (!fragment) {
    return null;
  }

  return IDS_BY_FRAGMENT.get(fragment.toLowerCase()) ?? null;
}

/**
 * Render-time counterpart, for components that hold the Prismic field itself
 * and want to branch before emitting an anchor (e.g. to render a `<button>`).
 */
export function resolveModalIdFromLink(
  field: LinkField | null | undefined,
): ModalId | null {
  return resolveModalIdFromHref(field ? asLink(field) : undefined);
}
